/**
 * Domain normalisation, shared between apps/api and apps/web (D2 multi-domain).
 *
 * This lives in @brokly/shared rather than in either app because BOTH sides need the
 * exact same rule and they must not drift: the api keys the `email_domain` table on the
 * normalised value (and the inbound-mail resolver looks it up by that key), while the web
 * shows the broker what will actually be stored and validates before the round trip.
 * Two copies of this rule would eventually disagree, and the failure would be silent:
 * mail from a domain the UI claims is registered simply would not match.
 *
 * [Source: apps/api/src/modules/firms/firm-domains.constants.ts] (the sole api consumer)
 * [Source: apps/api/src/modules/intake/genesis/domain-mapping.ts] (what the key is used for)
 */

/**
 * Normalise a raw tab URL / host string to the bare host the resolver and the
 * `email_domain` table key on (lower-case, no scheme/path/port/userinfo, no `www.`).
 *
 *   'https://www.Acme.CH/team?x=1' -> 'acme.ch'
 *   'user@mail.acme.ch:443'        -> 'mail.acme.ch'
 *   'ACME.ch'                      -> 'acme.ch'
 *
 * Returns '' for input that normalises to nothing (e.g. 'https://'); callers must treat
 * that as invalid rather than storing it.
 *
 * Pure + dependency-free (D-SUBDOMAIN-ONE-LEVEL discipline: no public-suffix-list dep).
 */
export function normalizeDomain(input: string): string {
  let d = (input ?? '').trim().toLowerCase();
  // Strip scheme (http://, https://, etc.)
  d = d.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  // Strip path / query / fragment
  d = d.split(/[/?#]/)[0] ?? '';
  // Strip userinfo (user:pass@host)
  const at = d.lastIndexOf('@');
  if (at >= 0) d = d.slice(at + 1);
  // Strip port
  d = d.split(':')[0] ?? '';
  // Strip a single leading www.
  if (d.startsWith('www.')) d = d.slice(4);
  return d;
}
